import React, { useEffect, useRef, useState } from 'react';
import db from '../services/db';
import { useIsMobile } from '../hooks/useIsMobile';
import AppSidebar from './AppSidebar';

// Phase 4 — viewport-aware breakpoint that updates on resize/rotate.
export default function AppLayout({ employee, userRole, permissions = {}, onLogout, children, activeSection = 'dashboard', onSectionChange }) {
  const isMobile = useIsMobile(768);
  const loginAtRef        = useRef(null);
  const historyKeyRef     = useRef(null);
  const logoutReasonRef   = useRef('manual');
  const hbRef             = useRef(null);

  // Phase 4 Fix 6 — dedicated sign-out that writes session offline + closes
  // loginHistory BEFORE auth.signOut() invalidates the credentials. The unmount
  // cleanup runs AFTER signOut, so its writes were denied silently and sessions
  // stayed isOnline: true forever.
  const handleSignOut = async (reason = 'manual') => {
    logoutReasonRef.current = reason;
    if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
    if (employee?.id) {
      const lastSeen = Date.now();
      const loginAt  = loginAtRef.current || lastSeen;
      const duration = Math.round((lastSeen - loginAt) / 60000);
      try {
        await db.setDoc('sessions', employee.id, { isOnline: false, lastSeen }, { merge: true });
        if (historyKeyRef.current) {
          await db.updateSubdoc('sessions', employee.id, 'loginHistory', historyKeyRef.current, {
            logoutAt: lastSeen,
            duration,
            logoutReason: reason,
          });
        }
      } catch (_) { /* ignore — auth may already be invalid */ }
    }
    onLogout();
  };

  // Force logout check — Admin can force logout any employee
  useEffect(() => {
    if (!employee?.id) return;
    const u = db.subscribeDoc('sessions', employee.id, (data) => {
      if (data?.forceLogout === true) {
        // Phase 4 Fix 5 — clear the flag FIRST, then sign out. Previously the
        // update was fire-and-forget and signOut could invalidate auth before
        // the write landed, leaving forceLogout: true and re-firing next login.
        db.setDoc('sessions', employee.id, { forceLogout: false }, { merge: true })
          .catch(() => {})
          .finally(() => handleSignOut('forced'));
      }
    });
    return () => u();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

  // Online status + heartbeat + login history
  useEffect(() => {
    if (!employee?.id) return;
    const now = Date.now();
    loginAtRef.current = now;
    // setDoc merge: most staff have no sessions doc yet, and updateDoc rejects a missing doc.
    db.setDoc('sessions', employee.id, {
      isOnline: true,
      lastSeen: now,
      lastLogin: now,
    }, { merge: true }).catch(() => {});
    // Push a new loginHistory entry; remember the key so we can close it on unmount.
    db.pushSubdoc('sessions', employee.id, 'loginHistory', {
      loginAt: now,
      logoutAt: null,
      duration: null,
      logoutReason: null,
    })
      .then(newId => { historyKeyRef.current = newId; })
      .catch(() => { /* swallow */ });

    // Heartbeat every 2 minutes
    hbRef.current = setInterval(() => {
      db.setDoc('sessions', employee.id, { lastSeen: Date.now() }, { merge: true }).catch(() => {});
    }, 120000);

    const closeSession = (reason) => {
      const lastSeen = Date.now();
      db.setDoc('sessions', employee.id, { isOnline: false, lastSeen }, { merge: true }).catch(() => {});
      const loginAt = loginAtRef.current || lastSeen;
      const duration = Math.round((lastSeen - loginAt) / 60000); // minutes
      if (historyKeyRef.current) {
        db.updateSubdoc('sessions', employee.id, 'loginHistory', historyKeyRef.current, {
          logoutAt: lastSeen,
          duration,
          logoutReason: reason || logoutReasonRef.current || 'auto',
        }).catch(() => {});
      }
    };

    const handleUnload = () => closeSession(logoutReasonRef.current === 'forced' ? 'forced' : 'auto');
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
      window.removeEventListener('beforeunload', handleUnload);
      closeSession(logoutReasonRef.current || 'manual');
    };
  }, [employee?.id]);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F5F7' }}>
      {/* Sidebar */}
      <AppSidebar
        employee={employee}
        userRole={userRole}
        permissions={permissions}
        activeSection={activeSection}
        onSectionChange={onSectionChange}
        onLogout={handleSignOut}
      />

      {/* Main Content */}
      <div style={{
        flex: 1,
        marginLeft: isMobile ? 0 : 240,
        minHeight: '100vh',
        transition: 'margin-left 0.3s ease',
      }}>
        {children}
      </div>
    </div>
  );
}
